import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { SESSION_EXPIRED_EVENT, SESSION_RENEWED_EVENT, SETTINGS_ROUTE, pageSettingsPath, t, type PageSettingsEntry, type SettingsIndexEntryRow } from '@bms2/shared';
import { Icon } from '../../components/Icon';
import { RECORD_WRITTEN_EVENT, apiFetch } from '../records/api';
import { reportCaughtInBrowser } from '../../shell/systemErrors';
import { SETTINGS_PATH } from './api';
import styles from './settings.module.css';

/**
 * 🔒 Every Settings page lists its tile's entries beside it (decision 2792; T1611).
 * The list is the Index's own rows for the page's category, in the Index's order,
 * read from the same route the Index and the top bar's search read, so it never
 * names an entry the Index does not draw for this person.
 *
 * 🔨 The rows are read once and held above the page, never once a page: two
 * entries of one tile are two pages, and a read on each would put a request
 * between every click the rail exists to save. A write anywhere marks them stale,
 * because a module switched off hides entries, and the next page asks again while
 * it still draws what it holds. View as remounts the frame's outlet, and with it
 * this holder, so an emulated person never sees another person's entries.
 */
interface IndexRowsSource {
  rows: readonly SettingsIndexEntryRow[] | null;
  /** A Settings page asking for the rows; it reads only when nothing is held or what is held is stale. */
  want: () => void;
}

const IndexRowsContext = createContext<IndexRowsSource | null>(null);

export function SettingsIndexRowsProvider({ apiBaseUrl = '', children }: { apiBaseUrl?: string; children: ReactNode }) {
  const [rows, setRows] = useState<readonly SettingsIndexEntryRow[] | null>(null);
  const stale = useRef(true);
  const reading = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const outdated = () => {
      stale.current = true;
    };
    const names = [RECORD_WRITTEN_EVENT, SESSION_EXPIRED_EVENT, SESSION_RENEWED_EVENT];
    for (const name of names) window.addEventListener(name, outdated);
    return () => {
      for (const name of names) window.removeEventListener(name, outdated);
    };
  }, []);
  const want = useCallback(() => {
    if (!stale.current || reading.current) return;
    stale.current = false;
    reading.current = true;
    apiFetch(`${apiBaseUrl}${SETTINGS_PATH}/index`)
      .then(async (response) => {
        // A refused or failed read draws no rail; the next page asks again, and the Index says what went wrong.
        if (!response.ok) {
          stale.current = true;
          return;
        }
        const answer = (await response.json()) as { rows?: SettingsIndexEntryRow[] };
        setRows(Array.isArray(answer.rows) ? answer.rows : []);
      })
      .catch((error: unknown) => {
        stale.current = true;
        reportCaughtInBrowser('settings.rail', error);
      })
      .finally(() => {
        reading.current = false;
      });
  }, [apiBaseUrl]);
  const value = useMemo(() => ({ rows, want }), [rows, want]);
  return <IndexRowsContext.Provider value={value}>{children}</IndexRowsContext.Provider>;
}

/** The entries a page's rail and kind-line menu draw: the tile's rows, which one is this page, and which one this page sits under. */
export interface SettingsTile {
  category: string;
  rows: readonly SettingsIndexEntryRow[];
  /** The entry whose route is this address: drawn marked, and not a link. */
  current: string | null;
  /** The entry a deeper page sits under, such as one print template under Print Templates: drawn marked, and still a link up. */
  within: string | null;
}

/** The tile a route sits in, or null where no rail is drawn (decision 2792). */
export function tileFor(rows: readonly SettingsIndexEntryRow[] | null, category: string, pathname: string): SettingsTile | null {
  // 🔒 The pages that open outside Settings (the org chart, the Mail Settings window, Trash) draw no rail.
  if (rows === null || !pathname.startsWith(`${SETTINGS_ROUTE}/`)) return null;
  const own = rows.filter((row) => row.category === category);
  // 🔨 A tile of one entry draws nothing: a list of the page it is on says nothing the title does not.
  if (own.length < 2) return null;
  const current = own.find((row) => row.route === pathname) ?? null;
  const under = current ? [] : own.filter((row) => pathname.startsWith(`${row.route}/`)).sort((a, b) => b.route.length - a.route.length);
  return { category, rows: own, current: current?.id ?? null, within: under[0]?.id ?? null };
}

/** The page's tile, asking the holder for the rows on the first page that wants them. Null outside the frame, before the read lands, or where none is drawn. */
export function useSettingsTile(category: string): SettingsTile | null {
  const source = useContext(IndexRowsContext);
  const { pathname } = useLocation();
  const want = source?.want;
  const inside = pathname.startsWith(`${SETTINGS_ROUTE}/`);
  // Asked on every address, which reads nothing unless a write has made the held rows stale.
  useEffect(() => {
    if (inside) want?.();
  }, [want, inside, pathname]);
  const rows = source?.rows ?? null;
  return useMemo(() => tileFor(rows, category, pathname), [rows, category, pathname]);
}

/** One entry of a page's gear menu as it is drawn: where it leads and what it reads. */
export interface SettingsEntryLink {
  route: string;
  label: string;
}

/**
 * 🔒 The entries a page's gear lists (decision 2805; T1614), in the order the
 * page names them, and only those this person may open. An entry is shown where
 * the Index serves its row, named by the row's title or by its own name where it
 * carries one. The one entry outside Settings, which no Index row carries, is
 * shown where the Index served a row gated on the capability its page asks: the
 * server filtered the rows by that key, so a served one proves the person holds
 * it. Nothing is listed before the rows are read, so the gear appears whole or
 * not at all.
 */
export function settingsEntriesFor(entries: readonly PageSettingsEntry[] | undefined, rows: readonly SettingsIndexEntryRow[] | null): readonly SettingsEntryLink[] {
  if (!entries || entries.length === 0 || rows === null) return [];
  const links: SettingsEntryLink[] = [];
  for (const entry of entries) {
    const path = pageSettingsPath(entry.route);
    const row = rows.find((held) => held.route === path);
    if (row) {
      links.push({ route: entry.route, label: entry.labelKey ? t(entry.labelKey) : row.title });
    } else if (entry.labelKey && entry.capabilityKey && !path.startsWith(`${SETTINGS_ROUTE}/`) && rows.some((held) => held.permissionKey === entry.capabilityKey)) {
      links.push({ route: entry.route, label: t(entry.labelKey) });
    }
  }
  return links;
}

/**
 * The gear's entries for this person, asking the holder for the Index rows on
 * any page that names entries, not only under Settings (decision 2805). Empty
 * outside the frame, before the read lands, and where none is served.
 */
export function useSettingsEntries(entries: readonly PageSettingsEntry[] | undefined): readonly SettingsEntryLink[] {
  const source = useContext(IndexRowsContext);
  const { pathname } = useLocation();
  const want = source?.want;
  const asks = (entries?.length ?? 0) > 0;
  // Asked on every address, which reads nothing unless a write has made the held rows stale.
  useEffect(() => {
    if (asks) want?.();
  }, [want, asks, pathname]);
  const rows = source?.rows ?? null;
  return useMemo(() => settingsEntriesFor(entries, rows), [entries, rows]);
}

/** The tile's entries as links, the page's own marked and not a link. */
function TileEntries({ tile, onChoose }: { tile: SettingsTile; onChoose?: () => void }) {
  return (
    <ul className={styles.railList}>
      {tile.rows.map((row) => (
        <li key={row.id}>
          {row.id === tile.current ? (
            <span className={`${styles.railEntry} ${styles.railCurrent}`} aria-current="page" data-settings-rail-entry={row.route} data-settings-rail-current="page">
              {row.title}
            </span>
          ) : (
            <Link
              to={row.route}
              className={`${styles.railEntry} ${styles.railLink} ${row.id === tile.within ? styles.railCurrent : ''}`}
              data-settings-rail-entry={row.route}
              {...(row.id === tile.within ? { 'aria-current': 'location' as const, 'data-settings-rail-current': 'within' } : {})}
              {...(onChoose ? { onClick: onChoose } : {})}
            >
              {row.title}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}

/** 🔒 The rail at a page's left from laptop width (decision 2792): the category's name over its entries. */
export function SettingsRail({ tile }: { tile: SettingsTile }) {
  return (
    <nav className={styles.rail} aria-label={tile.category} data-settings-rail={tile.category}>
      {/* 🔒 An entry that carries the category's own name is the heading already, so the heading is not drawn above it (decision 6684). */}
      {tile.rows.some((row) => row.title === tile.category) ? null : <p className={styles.railHeading}>{tile.category}</p>}
      <TileEntries tile={tile} />
    </nav>
  );
}

/**
 * Where an open list stands: under the control that opened it, in the viewport's
 * coordinates, as the bar it hangs from does not scroll. A list hung from the
 * right end of a row lines up its right edge with the control's, so it never runs
 * past the window.
 */
type MenuPlacement = { top: number; left: number } | { top: number; right: number };

/** The control a list hangs from, or the one button inside it, which takes the focus back when the list closes on Escape. */
function focusTarget(anchor: HTMLElement | null): HTMLElement | null {
  if (anchor === null || anchor instanceof HTMLButtonElement) return anchor;
  return anchor.querySelector<HTMLElement>('button');
}

/**
 * 🔒 One list hung from a control (decisions 2792, 2805): the folded rail under
 * a Settings page's category and a page's gear menu both open, place and close
 * through it. It is placed before paint, focuses its first link, and closes on
 * Escape, on a Tab past either end of the list, on a press outside the control
 * and the list, and when the window resizes. 🔨 The list is placed on the page body, never inside the bar it hangs
 * from, which cuts off whatever runs past it.
 */
export function useHangingList<A extends HTMLElement>(align: 'start' | 'end' = 'start') {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<MenuPlacement | null>(null);
  const anchor = useRef<A>(null);
  const panel = useRef<HTMLElement>(null);
  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) focusTarget(anchor.current)?.focus();
  }, []);
  // Placed before paint, so the list never shows a frame away from its control.
  useLayoutEffect(() => {
    if (!open) return;
    const at = anchor.current?.getBoundingClientRect();
    if (!at) return;
    setPlacement(align === 'end' ? { top: at.bottom, right: document.documentElement.clientWidth - at.right } : { top: at.bottom, left: at.left });
  }, [open, align]);
  useEffect(() => {
    if (!open) return undefined;
    // The first entry is where reading starts; the page's own entry is not a link, so it takes no focus.
    panel.current?.querySelector<HTMLElement>('a')?.focus();
    const away = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!(anchor.current?.contains(target) ?? false) && !(panel.current?.contains(target) ?? false)) close(false);
    };
    const within = (node: EventTarget | null) => node instanceof Node && ((anchor.current?.contains(node) ?? false) || (panel.current?.contains(node) ?? false));
    /**
     * 🔨 T2041: the list is portalled to the end of the body, so the browser's own Tab order would leave the document from
     * its last link and skip the control from its first. Tab past either end closes the list and puts the focus back on
     * the control, where the next Tab continues the page. Escape is this list's only while the focus is in it or on its
     * control, so an Escape meant for a window above the page, or one a field already took, is left alone.
     */
    const keys = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented && within(event.target)) {
        close(true);
        return;
      }
      if (event.key !== 'Tab') return;
      // 🔨 T2051: Tab from the control itself goes into the open list, the next stop in reading order, and Shift+Tab from it leaves the list closed behind.
      if (event.target instanceof Node && anchor.current?.contains(event.target)) {
        const first = panel.current?.querySelector<HTMLElement>('a');
        if (!event.shiftKey && first) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey) {
          close(false);
        }
        return;
      }
      if (!panel.current?.contains(event.target as Node)) return;
      const links = [...panel.current.querySelectorAll<HTMLElement>('a')];
      const edge = event.shiftKey ? links[0] : links[links.length - 1];
      if (links.length > 0 && event.target === edge) {
        event.preventDefault();
        close(true);
      }
    };
    const moved = () => close(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', keys);
    window.addEventListener('resize', moved);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', keys);
      window.removeEventListener('resize', moved);
    };
  }, [open, close]);
  const style: CSSProperties | undefined = placement ?? undefined;
  return { open, toggle: () => setOpen((was) => !was), close, anchor, panel, style };
}

/**
 * 🔒 The rail folded into the category half of the kind line (decision 2792):
 * below laptop width on every page, and at every width on a page whose content
 * carries a rail or a wide table of its own. The category opens the same list
 * the rail draws. 🔨 The list is placed on the page body, never inside the title
 * banner, which cuts off whatever runs past it.
 */
export function SettingsTileMenu({ tile, folded }: { tile: SettingsTile; folded: boolean }) {
  const { open, toggle, close, anchor, panel, style } = useHangingList<HTMLButtonElement>();
  const panelId = useId();
  return (
    <span className={`${styles.kindMenu} ${folded ? styles.kindMenuAlways : ''}`} data-settings-tile-menu={folded ? 'folded' : 'narrow'}>
      <button
        ref={anchor}
        type="button"
        className={styles.kindMenuToggle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-settings-crumb="category-menu"
        onClick={toggle}
      >
        {tile.category}
        <Icon meaning="expand" size="inline" />
      </button>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <nav ref={panel} id={panelId} className={styles.kindMenuPanel} style={style} aria-label={tile.category} data-settings-tile-menu-panel={tile.category}>
              <TileEntries tile={tile} onChoose={() => close(false)} />
            </nav>,
            document.body,
          )
        : null}
    </span>
  );
}
